import { Checkbox, Radio, makeStyles } from '@material-ui/core';
import { WbTooltip } from '../WbTooltip';
import { MouseEvent, useMemo } from 'react';

interface SelectorProps<T> {
  row: T;
  selectedItems: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  disabled?: boolean;
  tooltipIfDisabled?: string;
  getRowId: (row: T) => string;
  selection: 'single' | 'multiple';
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0.5),
  },
}));

export const Selector = <T,>({
  row,
  selectedItems,
  setSelectedItems,
  disabled = false,
  tooltipIfDisabled,
  getRowId,
  selection,
}: SelectorProps<T>) => {
  const classes = useStyles();

  const isChecked = useMemo(() => {
    return selectedItems.some((i: T) => getRowId(i) === getRowId(row));
  }, [selectedItems, row, getRowId]);

  const handleChange = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (selection === 'single') {
      setSelectedItems?.([row]);
    } else if (isChecked) {
      setSelectedItems?.(
        selectedItems.filter((e: T) => getRowId(e) !== getRowId(row)),
      );
    } else {
      setSelectedItems?.([...selectedItems, row]);
    }
  };

  const ariaLabel = 'select row';

  const Content = () =>
    selection === 'single' ? (
      <Radio
        classes={{ root: classes.root }}
        onClick={e => handleChange(e)}
        checked={isChecked}
        color="primary"
        disabled={disabled}
        inputProps={{ 'aria-label': ariaLabel }}
      />
    ) : (
      <Checkbox
        classes={{ root: classes.root }}
        indeterminate={disabled}
        onClick={e => handleChange(e)}
        checked={isChecked}
        color="primary"
        disabled={disabled}
        inputProps={{ 'aria-label': ariaLabel }}
      />
    );

  return disabled && tooltipIfDisabled ? (
    <WbTooltip title={tooltipIfDisabled}>
      <span>
        <Content />
      </span>
    </WbTooltip>
  ) : (
    <Content />
  );
};
